/* BA: reveal page content as it scrolls into view.
 *
 * Marks content blocks and product cards, then fades and lifts them in as they
 * enter. Items sharing a parent stagger, so a product row arrives as a
 * sequence rather than all at once.
 *
 * Entrances play once. Content that has arrived stays arrived -- the Apple
 * behaviour -- so scrolling back up never replays the page. The performances
 * that DO replay (the grid's settle, the film) own their own machinery.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* The password page choreographs its own entrance in ba-password.css. Left to
     run here as well, this lifted the hero container 24px -- two animation
     systems over the same pixels, and the arm moved in instead of fading. It
     also puts an opacity transition on .border-style, which is a common
     ancestor of the glass bar and so becomes a backdrop root while animating. */
  if (document.querySelector('.password-main-content')) return;

  var MARK = 'data-ba-reveal';
  /* 150ms, up from 70. With the slower 1.5s transition a 70ms gap overlaps so
     heavily that a row arrives as one blur; at 150 each item is legibly its
     own beat. MAX_STEPS 6 so longer rows keep building rather than flattening
     out after the fifth. */
  var STAGGER_MS = 150;
  var MAX_STEPS = 6;

  /* Top-level content blocks of each section, plus cards individually.
     .resource-list is excluded because its items animate one by one; animating
     the list as well would run two transitions over the same pixels. */
  var TARGETS = [
    'main .section > *:not(.resource-list):not(.section-background):not(.custom-section-background)',
    'main .resource-list__item',

    /* The arm sections. Their wrappers are .shopify-section--ba-*, which the
       Horizon selector above never matched, so every one of them arrived with
       no entrance at all.

       Deliberately absent from this list: anything that draws itself from
       scroll position. The story's pinned figure, the lift-windows plot and the
       lift x ride grid all animate off progress already, and fading a 24px
       translate over the top of them would be two systems moving the same
       pixels -- the exact fault the password-page guard above exists to
       prevent, and it would shift the very rect those figures measure. Their
       headings and captions are fair game; their canvases are not. */
    '.ba-open__copy', '.ba-open__figure',
    '.ba-sys__eyebrow', '.ba-sys__h', '.ba-sys__body', '.ba-sys__point',
    '.ba-det__eyebrow', '.ba-det__h', '.ba-det__item',
    '.ba-grid__eyebrow', '.ba-grid__h', '.ba-grid__body',
    '.ba-win__eyebrow', '.ba-win__h', '.ba-win__note',
    '.ba-bars__eyebrow', '.ba-bars__h', '.ba-bars__ratio', '.ba-bars__note',
    '.ba-story__eyebrow', '.ba-story__h', '.ba-story__sub', '.ba-story__outro',
    '.ba-spec__head', '.ba-spec__table tbody tr', '.ba-spec__note'
  ].join(', ');

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var el = entry.target;
      if (entry.isIntersecting) {
        el.style.setProperty('--ba-reveal-delay', el.dataset.baStagger || '0ms');
        el.setAttribute(MARK, 'in');
        /* An entrance plays once. Apple's reveals work this way: content that
           has arrived stays arrived, and re-hiding it made every upward
           scroll replay the whole page on the way back down. Unobserving is
           what retires it. */
        observer.unobserve(el);
      }
    });
  }, {
    /* Reveal once the element has risen 12% above the bottom edge, so items
       animate as they come up the page rather than while still clipped.

       threshold stays at 0 deliberately. A ratio threshold caps out for any
       element taller than the viewport -- it can never become 8% visible --
       which would leave tall sections hidden for good. The rootMargin does
       the timing instead. */
    rootMargin: '0px 0px -12% 0px',
    threshold: 0
  });

  function collect() {
    var groups = new Map();

    document.querySelectorAll(TARGETS).forEach(function (el) {
      if (el.hasAttribute(MARK)) return;
      if (el.closest('header-component, .header, footer, .cart-drawer')) return;
      /* Never touch a self-drawing figure or the pinned story frame. */
      if (el.closest('.ba-story__pin, .ba-grid__figure, .ba-win__figure, .ba-bars__chart')) return;
      /* Never hide a zero-size element. Horizon renders empty slot containers
         (e.g. section-resource-list__content with no "View all" button), and
         one of those can never intersect, so it would sit at opacity 0
         permanently -- invisible now, but it would swallow the content the day
         that slot is filled. */
      if (!el.offsetWidth && !el.offsetHeight) return;

      var siblings = groups.get(el.parentElement) || 0;
      groups.set(el.parentElement, siblings + 1);

      el.dataset.baStagger = Math.min(siblings, MAX_STEPS) * STAGGER_MS + 'ms';
      el.style.setProperty('--ba-reveal-delay', el.dataset.baStagger);
      el.setAttribute(MARK, 'out');
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', collect);
  } else {
    collect();
  }
  /* Horizon re-renders sections in the editor and on some interactions. */
  document.addEventListener('shopify:section:load', collect);
})();
